import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref, ComponentType, ReactNode } from 'react';
import styled from 'styled-components';

import type { BaseProps, NoChildrenProp, ForwardProps, OmitStrict, TestIdProp } from '../../types';
import { useTestIds } from '../../hooks';
import { withTestIds } from '../../utils';

import { getTreeTestIds } from './Tree.test-ids';

export type TreeNode = {
  /** The id of the Node. Used as the key for the li element wrapping the Node. */
  id: string;
  /** Child nodes. */
  nodes?: TreeNode[];
  /** Props to apply to the li. */
  listItemProps?: ForwardProps;

  [key: string]: unknown;
};

export interface TreeProps<T extends TreeNode = TreeNode>
  extends BaseProps,
    NoChildrenProp,
    TestIdProp {
  /** An array of ParentNodes and Nodes that compose the layout of the Tree. */
  nodes: T[];
  /** A function that allows custom styling and behavior for all Nodes. */
  nodeRenderer?: ComponentType<
    {
      depth: number;
      subTree?: ReactNode;
      hasParentSibling: TreeItemProps['hasParentSibling'];
    } & OmitStrict<T, 'listItemProps'>
  >;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLUListElement>;
}

export const StyledTreeListItem = styled.li``;

export const StyledTreeList = styled.ul`
  &,
  & & {
    list-style: none;
    position: relative;
  }

  ${StyledTreeListItem} {
    position: relative;
  }
`;

const DefaultNodeRenderer: TreeProps['nodeRenderer'] = ({ data, subTree }) => {
  return (
    <>
      {data}
      {subTree}
    </>
  );
};

interface TreeItemProps extends TreeNode {
  nodeRenderer: NonNullable<TreeProps['nodeRenderer']>;
  depth: number;
  hasParentSibling: boolean;
}

interface TreeListProps extends Pick<TreeProps, 'nodes' | 'ref'> {
  nodeRenderer: NonNullable<TreeProps['nodeRenderer']>;
  depth: number;
}

let TreeList: ComponentType<TreeListProps>;

const TreeItem = ({
  listItemProps,
  depth,
  nodes,
  nodeRenderer: NodeRenderer,
  ...restProps
}: TreeItemProps) => {
  return (
    <StyledTreeListItem {...listItemProps} role='none'>
      <NodeRenderer
        {...restProps}
        depth={depth}
        nodes={nodes}
        subTree={nodes && <TreeList depth={depth + 1} nodes={nodes} nodeRenderer={NodeRenderer} />}
      />
    </StyledTreeListItem>
  );
};

TreeList = forwardRef(function TreeListFunction(
  { nodes, depth, nodeRenderer, ...restProps }: PropsWithoutRef<TreeListProps>,
  ref?: TreeProps['ref']
) {
  const hasParentSibling = nodes.some(node => !!node.nodes);
  return nodes.length > 0 ? (
    <StyledTreeList {...restProps} ref={ref} role={depth === 0 ? 'tree' : 'group'}>
      {nodes.map(node => (
        <TreeItem
          {...node}
          key={node.id}
          depth={depth}
          nodeRenderer={nodeRenderer}
          hasParentSibling={hasParentSibling}
        />
      ))}
    </StyledTreeList>
  ) : null;
}) as ComponentType<TreeListProps>;

const Tree = forwardRef(function Tree(
  { testId, nodeRenderer = DefaultNodeRenderer, ...restProps }: PropsWithoutRef<TreeProps>,
  ref: TreeProps['ref']
) {
  const testIds = useTestIds(testId, getTreeTestIds);

  return (
    <TreeList
      data-testid={testIds.root}
      {...restProps}
      ref={ref}
      depth={0}
      nodeRenderer={nodeRenderer}
    />
  );
}) as <T extends TreeNode = TreeNode>(props: TreeProps<T> & ForwardProps) => JSX.Element | null;

export default withTestIds(Tree, getTreeTestIds);
